import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  OwnerBrain,
  type BrainEnv,
  type BrainProposal,
} from "../../lib/apiTyped";
import { useAuth } from "../../hooks/useAuth";
import { useResource } from "../../lib/useResource";
import { useApiCall } from "../../lib/useToastError";
import { useNotifications } from "../../components/shared/NotificationsProvider";
import { PageHeader } from "../../components/AppLayout";
import { Card, Badge } from "../../components/shared/Card";
import { Button } from "../../components/shared/Button";
import { DataTable, type Column } from "../../components/shared/DataTable";
import { fmtNumber } from "../../lib/format";

// ---- shared bits ----

// Colour per proposal action so add/update/delete read at a glance.
const ACTION_COLOR: Record<string, string> = {
  add: "#0f7a3d",
  update: "#9a7d1e",
  delete: "#C61418",
};

const ACTION_LABEL: Record<string, string> = {
  add: "añadir",
  update: "actualizar",
  delete: "borrar",
};

function ErrorCard({ message }: { message: string }) {
  return (
    <Card className="p-5" style={{ borderColor: "rgba(198,20,24,0.25)" }}>
      <div className="text-sm" style={{ color: "#C61418" }}>
        {message}
      </div>
    </Card>
  );
}

// react-markdown with no rehype-raw → raw HTML is not rendered (XSS-safe). A
// small component map styles the output without the typography plugin.
function ProposalBody({ body }: { body: string }) {
  const text = body.trim();
  if (!text) {
    return (
      <div className="text-sm text-ink-3 italic">Sin cuerpo (borrado).</div>
    );
  }
  return (
    <div className="text-sm text-ink leading-relaxed">
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="mb-2">{children}</p>,
          ul: ({ children }) => (
            <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>
          ),
          h1: ({ children }) => (
            <h3 className="text-base font-black mt-2 mb-1">{children}</h3>
          ),
          h2: ({ children }) => (
            <h4 className="text-sm font-bold mt-2 mb-1">{children}</h4>
          ),
          h3: ({ children }) => (
            <h5 className="text-sm font-semibold mt-2 mb-1">{children}</h5>
          ),
          code: ({ children }) => (
            <code
              className="px-1 py-0.5 rounded text-xs"
              style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
            >
              {children}
            </code>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="underline"
            >
              {children}
            </a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

// ---- summary view ----

type SummaryRow = { bundle: string; count: number };

function SummaryView({
  env,
  onOpen,
}: {
  env?: BrainEnv;
  onOpen: (bundle: string) => void;
}) {
  const { data, loading, error } = useResource(
    () => OwnerBrain.summary(env),
    [env],
  );

  if (error) return <ErrorCard message={error.message} />;

  const columns: Column<SummaryRow>[] = [
    {
      header: "Área de conocimiento",
      render: (r) => <span className="font-semibold">{r.bundle}</span>,
    },
    {
      header: "Propuestas",
      align: "right",
      render: (r) => <Badge label={fmtNumber(r.count)} color="#0f0f0f" />,
    },
  ];

  return (
    <DataTable<SummaryRow>
      rows={data?.bundles ?? null}
      loading={loading}
      columns={columns}
      rowKey={(r) => r.bundle}
      rowActions={(r) => (
        <Button size="sm" onClick={() => onOpen(r.bundle)}>
          Revisar
        </Button>
      )}
      empty={{
        title: "Nada pendiente",
        description:
          "El asistente aún no ha propuesto cambios a tu conocimiento. Cuando aprenda algo de tus conversaciones, aparecerá aquí para que lo revises.",
      }}
    />
  );
}

// ---- detail view ----

function ProposalCard({
  env,
  bundle,
  proposal,
  onDone,
}: {
  env?: BrainEnv;
  bundle: string;
  proposal: BrainProposal;
  onDone: () => void;
}) {
  const { run } = useApiCall();
  const { confirm } = useNotifications();
  const [busy, setBusy] = useState(false);
  const { meta, file } = proposal;

  async function accept() {
    const ok = await confirm({
      title: "Aplicar a tu conocimiento",
      message: `Se aplicará a «${bundle}» (${ACTION_LABEL[meta.action] ?? meta.action} · ${meta.slug}) y quedará registrado. Podrás pedirnos revertirlo si hace falta.`,
      confirmLabel: "Aplicar",
      variant: "neutral",
    });
    if (!ok) return;
    setBusy(true);
    const res = await run(() => OwnerBrain.accept(bundle, file, env), {
      successMessage: "Cambio aplicado a tu conocimiento.",
    });
    setBusy(false);
    if (res) onDone();
  }

  async function reject() {
    const ok = await confirm({
      title: "Descartar propuesta",
      message: `Se descartará «${meta.slug}» y no se aplicará ningún cambio a tu conocimiento.`,
      confirmLabel: "Descartar",
      variant: "danger",
    });
    if (!ok) return;
    setBusy(true);
    const res = await run(() => OwnerBrain.reject(bundle, file, env), {
      successMessage: "Propuesta descartada.",
    });
    setBusy(false);
    if (res) onDone();
  }

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <Badge
          label={ACTION_LABEL[meta.action] ?? meta.action}
          color={ACTION_COLOR[meta.action] ?? "#0f0f0f"}
        />
        <Badge label={meta.type} color="#4b5563" />
        <span className="font-mono text-sm font-semibold text-ink">
          {meta.slug}
        </span>
        <span className="text-xs text-ink-3">
          · aprendido de {fmtNumber(Number(meta.captures) || 0)} conversaciones
        </span>
      </div>
      {meta.title && (
        <div className="text-sm text-ink-2 mb-1">{meta.title}</div>
      )}
      {meta.rationale && (
        <div className="text-xs text-ink-3 mb-3 italic">{meta.rationale}</div>
      )}

      <div
        className="rounded-xl p-4 mb-4"
        style={{ backgroundColor: "rgba(0,0,0,0.03)" }}
      >
        <ProposalBody body={meta.action === "delete" ? "" : proposal.body} />
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="danger" size="sm" onClick={reject} disabled={busy}>
          Descartar
        </Button>
        <Button variant="primary" size="sm" onClick={accept} disabled={busy}>
          Aplicar
        </Button>
      </div>
    </Card>
  );
}

function DetailView({ env, bundle }: { env?: BrainEnv; bundle: string }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data, loading, error } = useResource(
    () => OwnerBrain.list(bundle, env),
    [bundle, env, refreshKey],
  );

  if (error) return <ErrorCard message={error.message} />;
  if (loading && !data) {
    return (
      <Card className="py-16 flex items-center justify-center">
        <span className="text-sm text-ink-3">Cargando propuestas…</span>
      </Card>
    );
  }

  const proposals = data?.proposals ?? [];
  // Re-pull so the list stays in sync with the backend after a mutation.
  const onDone = () => setRefreshKey((k) => k + 1);

  if (proposals.length === 0) {
    return (
      <Card className="p-8 text-center">
        <div className="text-sm text-ink-2">
          No quedan propuestas en esta área.
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {proposals.map((p) => (
        <ProposalCard
          key={p.file}
          env={env}
          bundle={bundle}
          proposal={p}
          onDone={onDone}
        />
      ))}
    </div>
  );
}

// Platform-admin-only environment switch. Regular owners never see this — the
// server ignores `env` for anyone who is not platform_admin and forces prod.
function AdminEnvToggle({
  env,
  onChange,
}: {
  env: BrainEnv;
  onChange: (e: BrainEnv) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-3">
        Admin
      </span>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant={env === "prod" ? "primary" : "ghost"}
          onClick={() => onChange("prod")}
        >
          Prod
        </Button>
        <Button
          size="sm"
          variant={env === "dev" ? "primary" : "ghost"}
          onClick={() => onChange("dev")}
        >
          Dev
        </Button>
      </div>
    </div>
  );
}

// ---- page ----

export function OwnerBrainPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const bundle = searchParams.get("bundle");
  const { isRealAdmin } = useAuth();
  // Environment is prod for everyone; only a platform admin gets the toggle.
  // We pass `env` to the API solely for platform admins — the server enforces
  // this too, so a non-admin can never reach dev even by crafting a request.
  const [env, setEnv] = useState<BrainEnv>("prod");
  const apiEnv = isRealAdmin ? env : undefined;

  const openDetail = (b: string) => setSearchParams({ bundle: b });
  const backToSummary = () => setSearchParams({});

  return (
    <>
      <PageHeader
        title="Tu conocimiento"
        subtitle={
          bundle
            ? bundle
            : "Cambios que el asistente propone a partir de tus conversaciones"
        }
        action={
          isRealAdmin ? (
            <AdminEnvToggle env={env} onChange={setEnv} />
          ) : undefined
        }
      />

      {isRealAdmin && env === "dev" && (
        <div
          className="mb-4 rounded-xl px-4 py-2 text-xs font-semibold"
          style={{ backgroundColor: "rgba(200,169,81,0.15)", color: "#8a6d00" }}
        >
          Entorno de pruebas (dev) · operando sobre el backend de desarrollo.
        </div>
      )}

      {!bundle && (
        <div className="mb-4 rounded-xl px-4 py-3 text-xs text-ink-2 bg-surface-2">
          El asistente aprende de cómo trabajáis y te propone añadir, afinar o
          retirar conocimiento. Nada se aplica sin tu visto bueno: revisa cada
          propuesta y decide.
        </div>
      )}

      {bundle && (
        <div className="mb-4">
          <Button size="sm" variant="ghost" onClick={backToSummary}>
            ← Volver
          </Button>
        </div>
      )}

      {bundle ? (
        <DetailView env={apiEnv} bundle={bundle} />
      ) : (
        <SummaryView env={apiEnv} onOpen={openDetail} />
      )}
    </>
  );
}
